import React, { useState } from 'react';
import './ScaledImage.css';

interface ScaledImageProps {
  src: string;
  alt?: string;
  caption?: string;
  className?: string;
  thumbnailClassName?: string;
  loading?: 'lazy' | 'eager';
  maxWidth?: string; // e.g., '280px', '740px', '100%' - default is medium (740px)
}

/**
 * ScaledImage - A reusable component that displays images at a small inline size
 * (credit card size or smaller) and expands to full size in a modal on click.
 * 
 * Features:
 * - Small inline thumbnail with hover indication
 * - Click to view full size in modal
 * - Keyboard accessible (Escape to close)
 * - Mobile-friendly
 * - Smooth animations
 * 
 * @param src - Image source URL
 * @param alt - Alternative text for accessibility
 * @param caption - Optional caption displayed below thumbnail
 * @param className - Additional classes for the container
 * @param thumbnailClassName - Additional classes for the thumbnail
 * @param loading - Image loading strategy (default: 'lazy')
 */
// Global cache to track images that have already loaded successfully
const loadedImagesCache = new Set<string>();

const ScaledImage: React.FC<ScaledImageProps> = ({
  src,
  alt = 'Image',
  caption,
  className = '',
  thumbnailClassName = '',
  loading = 'lazy',
  maxWidth = '280px' // Default to 280px
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Check cache first - if image already loaded, don't show loading state
  const [imageLoaded, setImageLoaded] = useState(() => loadedImagesCache.has(src));
  const [imageError, setImageError] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  if (imageError || !src) {
    return null; // Hide broken images
  }

  // Hide images if maxWidth is 'hidden'
  if (maxWidth === 'hidden') {
    return null;
  }

  return (
    <>
      {/* Thumbnail Container */}
      <div className={`scaled-image-container ${className}`}>
        <button
          className={`scaled-image-thumbnail ${thumbnailClassName}`}
          onClick={openModal}
          aria-label={`View full size: ${alt}`}
          type="button"
          style={{ 
            width: maxWidth,
            maxWidth: '100%',
            // Reserve the box before the image loads. A min-height that later
            // gives way to the natural height shifts everything below it, which
            // is what made scrolling past images jump.
            aspectRatio: '4 / 3',
            backgroundColor: imageLoaded ? 'transparent' : 'rgba(0, 0, 0, 0.05)'
          }}
        >
          <img
            src={src}
            alt={alt}
            loading={loading}
            className={`scaled-image-img ${imageLoaded ? 'loaded' : 'loading'}`}
            style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s ease-in' }}
            onLoad={() => {
              loadedImagesCache.add(src);
              setImageLoaded(true);
            }}
            onError={() => setImageError(true)}
          />
          {/* Zoom indicator on hover */}
          <div className="scaled-image-zoom-indicator" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="zoom-icon"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </div>
        </button>
        {caption && <p className="scaled-image-caption">{caption}</p>}
      </div>

      {/* Full Size Modal */}
      {isModalOpen && (
        <div
          className="scaled-image-modal"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Full size image"
        >
          <div className="scaled-image-modal-backdrop" />
          <div className="scaled-image-modal-content">
            {/* Close button */}
            <button
              className="scaled-image-close-btn"
              onClick={closeModal}
              aria-label="Close full size image"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Full size image */}
            <img
              src={src}
              alt={alt}
              className="scaled-image-full"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
            />

            {caption && (
              <p className="scaled-image-modal-caption">{caption}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScaledImage;

